import { dateTimeToView } from "helpers/date";
import {
    bidPointTypesEnum,
    bidStatusesEnum,
    organizationFlags,
    organizationFlagsEnum,
    routeTypesEnum,
} from "helpers/enums";
import { hiddenUserData } from "helpers/hiddenData";
import { flagVal, formatNumber } from "helpers/system";
import compact from "lodash/compact";
import moment from "moment";
import Tooltip from "rc-tooltip";
import React, { Component } from "react";

const useOdometerMileageFlagVal = flagVal(organizationFlags, organizationFlagsEnum.UseOdometerMileage);

export default class RouteInfo extends Component {
    planEmptyMileageDistance = (withStartPoint) => {
        if (!withStartPoint) {
            return <div className="b-point__info-item b-point__info-item--hidden-sm" />;
        }

        const { emptyMileageDistance } = this.props.route;
        const fullkms = Math.round(emptyMileageDistance / 1000);
        return (
            <div className="b-point__info-item">
                <div className="b-point__info-item--value-wrap">
                    <div className="b-point__info-item--value" data-test-id="plan-empty-mileage-distance">
                        {formatNumber(fullkms)}
                    </div>
                    <div className="b-point__info-item--unit">&nbsp;км</div>
                </div>
                <div className="b-point__info-item--title">Порожний пробег</div>
            </div>
        );
    };

    planDistance = () => {
        const { distanceWithoutEmptyMileage, distance, emptyMileageDistance } = this.props.route;
        const fullkms = emptyMileageDistance
            ? Math.round(distanceWithoutEmptyMileage / 1000)
            : distance
              ? Math.round(distance / 1000)
              : 0;
        return (
            <div className="b-point__info-item">
                <div className="b-point__info-item--value-wrap">
                    <div className="b-point__info-item--value" data-test-id="plan-distance">
                        {formatNumber(fullkms)}
                    </div>
                    <div className="b-point__info-item--unit">&nbsp;км</div>
                </div>
                <div className="b-point__info-item--title">Расстояние</div>
            </div>
        );
    };

    factDistance = () => {
        const { activeMileage } = this.props;
        const activeKms = activeMileage ? Math.round(activeMileage / 1000) : 0;
        return (
            <div className="b-point__info-item">
                <div className="b-point__info-item--value-wrap">
                    <div className="b-point__info-item--value" data-test-id="fact-distance">
                        {formatNumber(activeKms)}
                    </div>
                    <div className="b-point__info-item--unit">&nbsp;км</div>
                </div>
                <div className="b-point__info-item--title">Пробег</div>
            </div>
        );
    };

    factEmptyMileageDistance = () => {
        const { factEmptyMileageBefore, factEmptyOdometerMileageBefore, organization } = this.props;
        const useOdometerMileageEnabled =
            (organization.intFlags & useOdometerMileageFlagVal) === useOdometerMileageFlagVal;
        const emptyMileageValue = useOdometerMileageEnabled ? factEmptyOdometerMileageBefore : factEmptyMileageBefore;
        const fullKms = emptyMileageValue ? Math.round(emptyMileageValue / 1000) : 0;
        if (fullKms > 0) {
            return (
                <div className="b-point__info-item">
                    <div className="b-point__info-item--value-wrap">
                        <div className="b-point__info-item--value" data-test-id="fact-empty-mileage-distance">
                            {formatNumber(fullKms)}
                        </div>
                        <div className="b-point__info-item--unit">&nbsp;км</div>
                    </div>
                    <div className="b-point__info-item--title">Порожний пробег</div>
                </div>
            );
        } else {
            return <div className="b-point__info-item" />;
        }
    };

    planPricePerKm = () => {
        const {
            route: { distanceWithoutEmptyMileage, distance },
            price,
            roles,
            organization,
        } = this.props;
        const planPricePerKm = distanceWithoutEmptyMileage
            ? formatNumber(price / (distanceWithoutEmptyMileage / 1000))
            : distance
              ? formatNumber(price / (distance / 1000))
              : 0;
        return (
            <div className="b-point__info-item">
                <div className="b-point__info-item--value-wrap">
                    <div className="b-point__info-item--value" data-test-id="plan-price-per-km">
                        {hiddenUserData(
                            formatNumber(planPricePerKm),
                            { valueMask: "xx,xx" },
                            roles.isTruckingOrganizationDispatcher,
                        )}
                    </div>
                    <div className="b-point__info-item--unit">&nbsp;{organization?.currencyData?.sign}</div>
                </div>
                <div className="b-point__info-item--title">{organization?.currencyData?.name}/км</div>
            </div>
        );
    };

    factPricePerKm = () => {
        const { activeMileage, price, status, roles, organization } = this.props;
        const factPricePerKm = activeMileage ? formatNumber(price / (activeMileage / 1000)) : 0;
        return (
            <div className="b-point__info-item">
                <div className="b-point__info-item--value-wrap">
                    {status === bidStatusesEnum.Done ? (
                        <>
                            <div className="b-point__info-item--value" data-test-id="fact-price-per-km">
                                {hiddenUserData(
                                    formatNumber(factPricePerKm),
                                    { valueMask: "xx,xx" },
                                    roles.isTruckingOrganizationDispatcher,
                                )}
                            </div>
                            <div className="b-point__info-item--unit">&nbsp;{organization?.currencyData?.sign}</div>
                        </>
                    ) : (
                        <div className="b-point__info-item--value" data-test-id="fact-price-per-km">
                            -
                        </div>
                    )}
                </div>
                <div className="b-point__info-item--title">
                    {organization?.currencyData?.name}/км
                    <Tooltip placement="bottom" overlay="Показывается только у выполненной заявки">
                        <i className="icon-uEA69-question-circle b-point__tooltip-icon" />
                    </Tooltip>
                </div>
            </div>
        );
    };

    planDuration = () => {
        const { points } = this.props;
        const firstPoint = points.find((point) => point.type === bidPointTypesEnum.LoadPoint);
        const lastPoint = points[points.length - 1];
        const firstPointLeaveDate = firstPoint?.planLeaveDateOffset;

        let hour;
        let minutes;
        if (firstPointLeaveDate) {
            const duration = (moment(lastPoint.planEnterDateOffset) - moment(firstPointLeaveDate)) / 1000;
            [hour, minutes] = this.getTimeFromSeconds(duration);
        }
        return (
            <div className="b-point__info-item">
                <div className="b-point__info-item--value-wrap">
                    {firstPointLeaveDate ? (
                        <>
                            <div className="b-point__info-item--value" data-test-id="plan-duration-hours">
                                {hour}
                            </div>
                            <div className="b-point__info-item--unit">&nbsp;ч&nbsp;</div>
                            <div className="b-point__info-item--value" data-test-id="plan-duration-minutes">
                                {minutes}
                            </div>
                            <div className="b-point__info-item--unit">&nbsp;м</div>
                        </>
                    ) : (
                        <div className="b-point__info-item--value" data-test-id="plan-duration-hours">
                            -
                        </div>
                    )}
                </div>
                <div className="b-point__info-item--title">
                    Время в пути
                    <Tooltip
                        placement="bottom"
                        overlay="Время от даты выезда из первой до даты въезда в последнюю точку"
                    >
                        <i className="icon-uEA69-question-circle b-point__tooltip-icon" />
                    </Tooltip>
                </div>
            </div>
        );
    };

    getTimeFromSeconds = (duration) => {
        const negative = duration < 0;
        if (negative) {
            duration = -duration;
        }
        const minute = Math.floor(duration / 60);
        const rest = minute % 60;
        const hour = Math.floor(minute / 60);
        const minutes = rest > 9 ? rest : `0${rest}`;
        return [`${negative ? "-" : ""}${hour}`, minutes];
    };

    getEarlyPointFrom = (timeArr) => {
        const notNullTime = compact(timeArr);
        if (notNullTime.length === 0) {
            return null;
        }
        const momentTimes = notNullTime.map((t) => moment(t));
        const minTime = moment.min(momentTimes);
        return notNullTime[momentTimes.indexOf(minTime)];
    };

    factDuration = () => {
        const { points } = this.props;
        const firstPoint = points.find((point) => point.type === bidPointTypesEnum.LoadPoint);
        const lastPoint = points[points.length - 1];
        const startDate = firstPoint?.loadUnloadedAtByLogist || firstPoint?.autoLeavedAt || firstPoint?.loadUnloadedAt;
        let endDate = lastPoint.enteredAtByLogist || lastPoint.autoEnteredAt || lastPoint.enteredAt;

        let duration;
        let hour;
        let minutes;
        if (startDate) {
            endDate = endDate ? moment(endDate) : moment();
            duration = (endDate - moment(startDate)) / 1000;
            [hour, minutes] = this.getTimeFromSeconds(duration);
        }
        return (
            <div className="b-point__info-item">
                <div className="b-point__info-item--value-wrap">
                    {startDate ? (
                        <>
                            <div className="b-point__info-item--value" data-test-id="fact-duration-hours">
                                {hour}
                            </div>
                            <div className="b-point__info-item--unit">&nbsp;ч&nbsp;</div>
                            <div className="b-point__info-item--value" data-test-id="fact-duration-minutes">
                                {minutes}
                            </div>
                            <div className="b-point__info-item--unit">&nbsp;м</div>
                        </>
                    ) : (
                        <div className="b-point__info-item--value" data-test-id="fact-duration-hours">
                            -
                        </div>
                    )}
                </div>
                <div className="b-point__info-item--title">
                    Время в пути
                    <Tooltip placement="bottom" overlay="Фактическое время в пути">
                        <i className="icon-uEA69-question-circle b-point__tooltip-icon" />
                    </Tooltip>
                </div>
            </div>
        );
    };

    planFinishDate = (planEnterDateOffset) => {
        return (
            <div className="b-point__info-item">
                <div className="b-point__info-item--value-wrap">
                    <div className="b-point__info-item--value" data-test-id="plan-finish-date">
                        {dateTimeToView(planEnterDateOffset, "DD.MM.YY")}
                    </div>
                    <div className="b-point__info-item--unit" data-test-id="plan-finish-time">
                        &nbsp;{dateTimeToView(planEnterDateOffset, "HH:mm")}
                    </div>
                </div>
                <div className="b-point__info-item--title">
                    Время прибытия
                    <Tooltip placement="bottom" overlay="Время въезда в последнюю точку по заявке">
                        <i className="icon-uEA69-question-circle b-point__tooltip-icon" />
                    </Tooltip>
                </div>
            </div>
        );
    };

    factFinishDate = () => {
        const {
            points,
            hasEstimationData,
            estimations,
            bid: { isExpressBid },
            workRestInfo: { workHours, averageSpeed },
        } = this.props;
        const estimation = hasEstimationData
            ? estimations.find((x) => x.bidPointId === points[points.length - 1].id)
            : null;
        const estimatedDate = estimation?.estimatedDate;

        let showWorkHours = workHours;
        let showAverageSpeed = averageSpeed;
        if (isExpressBid) {
            const {
                bid: {
                    expressBid: { settings },
                },
            } = this.props;
            showWorkHours = settings.workHours;
            showAverageSpeed = settings.averageSpeed;
        }
        const { enteredAt, autoEnteredAt, enteredAtByLogist } = points[points.length - 1];
        const enterDate = enteredAtByLogist || autoEnteredAt || enteredAt;
        const enterDateDate = dateTimeToView(enterDate || estimatedDate, "DD.MM.YY");
        const enterDateTime = dateTimeToView(enterDate || estimatedDate, "HH:mm");
        const tooltipText =
            showWorkHours && showAverageSpeed ? (
                <div>
                    Время рассчитывается с учетом настроек {isExpressBid ? "Экспресс - заявки" : "компании"}:
                    <br />
                    {showAverageSpeed}км/ч - средняя скорость машины
                    <br />
                    {showWorkHours}ч - время работы водителя в день
                    <br />
                    {showAverageSpeed * showWorkHours}км - средний пробег в сутки
                </div>
            ) : (
                <div>Время рассчитывается с учетом настроек {isExpressBid ? "Экспресс - заявки" : "компании"}</div>
            );
        return (
            <div className="b-point__info-item">
                <div className="b-point__info-item--value-wrap">
                    <div className="b-point__info-item--value" data-test-id="fact-finish-date">
                        {enterDateDate}
                    </div>
                    {enterDateTime !== "-" && (
                        <div className="b-point__info-item--unit" data-test-id="fact-finish-time">
                            &nbsp;{enterDateTime}
                        </div>
                    )}
                </div>
                <div className="b-point__info-item--title">
                    {enterDate ? (
                        <>
                            Время прибытия
                            <Tooltip placement="left" overlay="Время фактического въезда в последнюю точку">
                                <i className="icon-uEA69-question-circle b-point__tooltip-icon" />
                            </Tooltip>
                        </>
                    ) : (
                        <>
                            Расчетное время прибытия
                            <Tooltip placement="left" overlay={tooltipText}>
                                <i className="icon-uEA69-question-circle b-point__tooltip-icon" />
                            </Tooltip>
                        </>
                    )}
                </div>
            </div>
        );
    };

    render() {
        const { points, status, hasPlanEmptyMileageBidModule, organization } = this.props;
        if (!points || points.length === 0) {
            return null;
        }
        const { planEnterDateOffset } = points[points.length - 1];
        const withStartPoint = (this.props.route?.segments || []).some(
            (item) => item.routeType === routeTypesEnum.EmptyMileage,
        );
        const showFactEmptyMileageDistance =
            withStartPoint || (status === bidStatusesEnum.Done && hasPlanEmptyMileageBidModule);
        const useOdometerMileageEnabled =
            (organization.intFlags & useOdometerMileageFlagVal) === useOdometerMileageFlagVal;
        const tooltipText = "Пробег рассчитан на основе одометра";
        return (
            <div className="b-point__table-container">
                <div className="b-point__table--plan-stats">
                    <span className="b-point__table--header">Плановые показатели</span>
                </div>
                <div className="b-point__table--fact-stats">
                    <span className="b-point__table--header">Фактические показатели</span>
                    {useOdometerMileageEnabled && (
                        <Tooltip placement="bottom" overlay={tooltipText}>
                            <i className="icon-uEA69-question-circle b-point__tooltip-icon" />
                        </Tooltip>
                    )}
                </div>
                <div className="b-point__table-empty--left" />
                <div className="b-point__table-empty--between" />
                <div className="b-point__table-empty--right" />
                {this.planEmptyMileageDistance(withStartPoint)}
                {this.planDistance()}
                {this.planPricePerKm()}
                {this.planDuration()}
                {this.planFinishDate(planEnterDateOffset)}
                {!withStartPoint && <div className="b-point__info-item" />}
                {withStartPoint && <div className="b-point__info-item--padding" />}
                {showFactEmptyMileageDistance && this.factEmptyMileageDistance()}
                {this.factDistance()}
                {this.factPricePerKm()}
                {this.factDuration()}
                {this.factFinishDate()}
            </div>
        );
    }
}
